<template>
  <div
    :class="false ? 'absolute opacity-0 pointer-events-none' : 'opacity-100'"
    class="z-10"
  >
    <div class="">
      <div class="border-b border-truegray-300 pb-6">
        <h2>Subscriber Activity</h2>
        <p class="text-sm text-truegray-500">
          {{ config.subscriber.email }}
        </p>
      </div>
      <div v-if="loadedData">
        <div class="border-b border-truegray-300 pt-6 pb-8">
          <h3>Newsletter Stats</h3>
          <div class="flex">
            <div class="w-1/2 pb-6 pr-4 flex flex-col justify-center">
              <div>
                <div class="flex items-center space-x-2 pb-1">
                  <p class="text-xs text-truegray-400">Total received:</p>
                  <p>{{ totalReceived }}</p>
                </div>

                <div class="flex items-center space-x-2 pb-1">
                  <p class="text-xs text-truegray-400">Total opened:</p>
                  <p>{{ totalOpened }}</p>
                </div>

                <div class="flex items-center space-x-2 pb-1">
                  <p class="text-xs text-truegray-400">Total clicked:</p>
                  <p>{{ totalClicked }}</p>
                </div>

                <div
                  :class="[
                    'flex space-x-2 mt-2',
                    { 'items-center': subscriberLists.length < 3 },
                  ]"
                >
                  <p class="text-xs text-truegray-400">Lists:</p>
                  <p
                    v-if="subscriberLists"
                    class="flex items-center flex-wrap gap-y-2"
                  >
                    <span
                      v-for="list in subscriberLists"
                      :key="list.id"
                      class="py-1 px-2 rounded-xl mr-2 border border-primary text-xs text-primary"
                    >
                      {{ list.name }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div v-if="showStats" class="flex justify-center w-1/2">
              <div class="flex space-x-6">
                <div>
                  <div class="mb-2">
                    <vue-ellipse-progress
                      v-if="ellipseConfig.opened"
                      v-bind="ellipseConfig.opened"
                      :progress="percentOpened"
                    >
                      <template #legend-value>
                        <span slot="legend-value">%</span>
                      </template>
                      <template #legend-caption>
                        <p class="text-xs text-truegray-500 italic">Opened</p>
                      </template>
                    </vue-ellipse-progress>
                  </div>
                  <p
                    class="text-center text-xs font-bold"
                    :style="{ color: ellipseConfig.opened.color }"
                  >
                    {{ ellipseConfig.opened.text }}
                  </p>
                </div>

                <div>
                  <div class="mb-2">
                    <vue-ellipse-progress
                      v-bind="ellipseConfig.clicked"
                      :progress="percentClicked"
                    >
                      <template #legend-value>
                        <span slot="legend-value">%</span>
                      </template>
                      <template #legend-caption>
                        <p class="text-xs text-truegray-500 italic">Clicked</p>
                      </template>
                    </vue-ellipse-progress>
                  </div>
                  <p
                    class="text-center text-xs font-bold"
                    :style="{ color: ellipseConfig.clicked.color }"
                  >
                    {{ ellipseConfig.clicked.text }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pt-6">
          <h3>Newsletter Inbox</h3>
          <div v-if="showStats" class="flex flex-col max-h-80 overflow-y-auto">
            <div
              v-for="newsletter in newsletters"
              :key="newsletter.newsletter.id"
              class="flex flex-col w-full"
            >
              <div
                class="flex justify-start align-start bg-white pr-6 pt-5 w-full text-left gap-4"
              >
                <div
                  class="flex flex-row justify-start align-start flex-grow space-x-6"
                >
                  <div
                    class="flex items-center justify-center seperator-right relative w-8"
                  >
                    <h6 class="">#{{ newsletter.newsletter.id }}</h6>
                  </div>
                  <div class="flex flex-col text-black leading-4">
                    <p
                      :style="{ fontSize: '12px' }"
                      :class="['text-sm', 'clamp-1', 'newsletter-post-title']"
                    >
                      {{ newsletter.newsletter.title }}
                    </p>
                    <p
                      v-if="newsletter.newsletter.subject"
                      :class="['clamp-1 newsletter-body-text']"
                    >
                      {{ newsletter.newsletter.subject }}
                    </p>
                    <!--
                      <p 
                      v-if="newsletter.newsletter.blurb" 
                      :class="[
                      'clamp-1'
                      ]"
                      >
                      {{ newsletter.newsletter.blurb }}
                      </p> 
                    -->
                  </div>
                </div>
                <div class="self-center">
                  <span
                    v-if="newsletter.openedAt"
                    class="text-green-400"
                    :title="`Opened on ${getDateFormatted(
                      newsletter.openedAt,
                    )}`"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-5 h-5"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M2.106 6.447A2 2 0 001 8.237V16a2 2 0 002 2h14a2 2 0 002-2V8.236a2 2 0 00-1.106-1.789l-7-3.5a2 2 0 00-1.788 0l-7 3.5zm1.48 4.007a.75.75 0 00-.671 1.342l5.855 2.928a2.75 2.75 0 002.46 0l5.852-2.926a.75.75 0 10-.67-1.342l-5.853 2.926a1.25 1.25 0 01-1.118 0l-5.856-2.928z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                  <span v-else class="text-truegray-300" title="Not opened">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-5 h-5"
                    >
                      <path
                        d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z"
                      />
                      <path
                        d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z"
                      />
                    </svg>
                  </span>
                </div>
                <div class="flex self-center gap-6">
                  <div
                    v-if="newsletter.clickedAt"
                    :title="`Clicked on ${getDateFormatted(
                      newsletter.clickedAt,
                    )}`"
                    class="flex text-green-400"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-5 h-5 mr-1 self-center"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 1a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 1zM5.05 3.05a.75.75 0 011.06 0l1.062 1.06A.75.75 0 116.11 5.173L5.05 4.11a.75.75 0 010-1.06zm9.9 0a.75.75 0 010 1.06l-1.06 1.062a.75.75 0 01-1.062-1.061l1.061-1.06a.75.75 0 011.06 0zM3 8a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 013 8zm11 0a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 0114 8zm-6.828 2.828a.75.75 0 010 1.061L6.11 12.95a.75.75 0 01-1.06-1.06l1.06-1.06a.75.75 0 011.06 0zm3.594-3.317a.75.75 0 00-1.37.364l-.492 6.861a.75.75 0 001.204.65l1.043-.799.985 3.678a.75.75 0 001.45-.388l-.978-3.646 1.292.204a.75.75 0 00.74-1.16l-3.874-5.764z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>

                  <div v-else title="No clicks" class="flex text-truegray-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-5 h-5 mr-1 self-center"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 1a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 1zM5.05 3.05a.75.75 0 011.06 0l1.062 1.06A.75.75 0 116.11 5.173L5.05 4.11a.75.75 0 010-1.06zm9.9 0a.75.75 0 010 1.06l-1.06 1.062a.75.75 0 01-1.062-1.061l1.061-1.06a.75.75 0 011.06 0zM3 8a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 013 8zm11 0a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 0114 8zm-6.828 2.828a.75.75 0 010 1.061L6.11 12.95a.75.75 0 01-1.06-1.06l1.06-1.06a.75.75 0 011.06 0zm3.594-3.317a.75.75 0 00-1.37.364l-.492 6.861a.75.75 0 001.204.65l1.043-.799.985 3.678a.75.75 0 001.45-.388l-.978-3.646 1.292.204a.75.75 0 00.74-1.16l-3.874-5.764z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <!--
                    <div
                    v-else-if="!newsletter.openedAt"
                    :title="`Clicked on ${getDateFormatted(newsletter.clickedAt)}`"
                    class="flex h-6 px-2 rounded-xl border border-red-500 text-xs text-red-400"
                    >
                    <div class="flex flex-col self-center">
                    <h6>Unread</h6>
                    </div>
                    </div> 
                  -->
                  <!--
                    <div
                    v-if="newsletter.newsletter.sentAt"
                    class="flex h-6 px-2 rounded-xl border border-black text-xs text-black"
                    >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 mr-1 self-center">
                    <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                    </svg>

                    <div class="flex flex-col self-center">
                    <h6>{{ getDateFormatted(newsletter.newsletter.sentAt) }}</h6>
                    </div>
                    </div> 
                  -->
                  <div class="flex flex-col text-right">
                    <h6 class="text-black">
                      {{ getDateFormatted(newsletter.newsletter.sentAt) }}
                    </h6>
                    <h6 class="text-truegray-400">Sent</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex">
            <p class="text-xs text-truegray-400">No newsletters received.</p>
          </div>
        </div>
      </div>
      <div v-else class="w-full h-14 mt-4">
        <lottie :options="loadingAnimationOptions" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as loadingAnimation from '../../../../../../shared/components/loading-dark.json'
import { VueEllipseProgress } from 'vue-ellipse-progress'
import { getDateFormatted } from '../../../../../../js/util/helper'

export default {
  components: {
    VueEllipseProgress,
  },

  props: {
    config: { default: null },
  },

  data() {
    return {
      loadedData: false,
      loadingAnimationOptions: {
        animationData: loadingAnimation.default,
      },

      // newsletters
      newsletters: [],

      // stats
      percentConfig: [
        {
          limitOpened: 100,
          limitClicked: 100,
          color: '#31C48D',
          text: 'Amazing',
        },
        {
          limitOpened: 30,
          limitClicked: 5,
          color: '#4ADE80',
          text: 'Great',
        },
        {
          limitOpened: 20,
          limitClicked: 3,
          color: '#FACC15',
          text: 'Not bad',
        },
        {
          limitOpened: 15,
          limitClicked: 2,
          color: '#38BDF8',
          text: 'Could be better...',
        },
      ],

      configOpened: {
        100: {
          color: '#31C48D',
          text: 'Amazing',
        },

        30: {
          color: '#4ADE80',
          text: 'Great',
        },

        20: {
          color: '#FACC15',
          text: 'Not bad',
        },

        15: {
          color: '#38BDF8',
          text: 'Could be better...',
        },
      },

      configClicked: {
        100: {
          color: '#31C48D',
          text: 'Amazing',
        },

        5: {
          color: '#4ADE80',
          text: 'Great',
        },

        3: {
          color: '#FACC15',
          text: 'Not bad',
        },

        2: {
          color: '#38BDF8',
          text: 'Could be better...',
        },
      },

      ellipseConfig: {
        opened: null,
        clicked: null,
      },

      ellipseConfigDefault: {
        'color': '',
        'empty-color': '#D4D4D4',
        'size': 120,
        'thickness': 5,
        'empty-thickness': 3,
        'line-mode': 'out 5',
        'animation': 'default 1500 250',
        'font-size': '1.25rem',
        'font-color': '',
      },
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      getMailListsByFollowerEmail: 'careerwise/getMailListsByFollowerEmail',
    }),

    showStats() {
      // only show stats if at least one newsletter has data...
      return this.newsletters.some((newsletter) => !!newsletter.newsletter)
    },

    subscriberLists() {
      return this.getMailListsByFollowerEmail(this.config.subscriber.email)
    },

    totalReceived() {
      return this.newsletters.length
    },

    totalOpened() {
      return this.newsletters.filter((newsletter) => !!newsletter.openedAt)
        ?.length
    },

    totalClicked() {
      return this.newsletters.filter((newsletter) => !!newsletter.clickedAt)
        ?.length
    },

    percentOpened() {
      return Math.trunc((this.totalOpened / this.totalReceived) * 100)
    },

    percentClicked() {
      return Math.trunc((this.totalClicked / this.totalReceived) * 100)
    },
  },

  async mounted() {
    this.init()
  },

  methods: {
    ...mapActions({
      dispatchGetSubscriberCampaignStats:
        'careerwise/dispatchGetSubscriberCampaignStats',
    }),

    getDateFormatted,

    async init() {
      const payload = {
        email: this.config.subscriber.email,
      }

      this.$set(this, 'loadedData', false)
      const newsletters = (
        await this.dispatchGetSubscriberCampaignStats(payload)
      )
        // filter out campaigns without newsletter data...
        ?.filter((newsletter) => newsletter.newsletter)
      this.$set(this, 'newsletters', newsletters)
      this.setEllipseConfig()
      this.$set(this, 'loadedData', true)
    },

    setEllipseConfig() {
      const getConfig = (key) => {
        const colorDefault = this.settings?.primary_color || ''
        const percentKey = `percent${key}`
        const limitKey = `limit${key}`

        const percent = this[percentKey]
        const config = this.percentConfig
          .sort((a, b) => a[limitKey] - b[limitKey])
          .find((obj) => percent <= obj[limitKey])

        return {
          'color': config?.color || colorDefault,
          'font-color': config?.color || colorDefault,
          'text': config?.text || '',
        }
      }

      const configOpened = {
        ...this.ellipseConfigDefault,
        ...getConfig('Opened'),
      }

      const configClicked = {
        ...this.ellipseConfigDefault,
        ...getConfig('Clicked'),
      }

      this.$set(this.ellipseConfig, 'opened', configOpened)
      this.$set(this.ellipseConfig, 'clicked', configClicked)
    },
  },
}
</script>

<style scoped>
.clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
